import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { CompanySidebar } from '../components/Dashboard/CompanySidebar';
import { Loader2 } from 'lucide-react';



const COUNTRIES = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
    'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
    'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
    'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon',
    'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo',
    'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica',
    'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
    'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
    'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
    'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica',
    'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos',
    'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
    'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
    'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
    'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
    'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
    'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia',
    'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
    'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka',
    'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
    'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
    'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
    'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen', 'Zambia', 'Zimbabwe'
];

const HIRING_REGIONS = [
    'North America',
    'Europe',
    'Asia Pacific',
    'Middle East & Africa',
    'Latin America',
    'Global / Remote'
];

const COMPANY_SIZES = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees'
];

// Skeleton components for consistent loading UI
const HeaderSkeleton = () => (
    <div className="mb-10 animate-pulse">
        <div className="h-9 bg-slate-200 rounded-lg w-32 mb-3"></div>
        <div className="h-5 bg-slate-200 rounded w-80"></div>
    </div>
);

const ProfileCardSkeleton = () => (
    <section className="card-premium p-8">
        <div className="flex items-center justify-between mb-6">
            <div className="animate-pulse">
                <div className="h-7 bg-slate-200 rounded w-24 mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-48"></div>
            </div>
        </div>
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 animate-pulse">
                    <div className="h-4 bg-slate-200 rounded w-28"></div>
                    <div className="h-12 bg-slate-100 rounded-xl w-full"></div>
                </div>
                <div className="space-y-2 animate-pulse">
                    <div className="h-4 bg-slate-200 rounded w-24"></div>
                    <div className="h-12 bg-slate-100 rounded-xl w-full"></div>
                    <div className="h-3 bg-slate-100 rounded w-36"></div>
                </div>
            </div>
            <div className="flex justify-end pt-4 animate-pulse">
                <div className="h-10 bg-slate-200 rounded-xl w-36"></div>
            </div>
        </div>
    </section>
);

const DeleteCardSkeleton = () => (
    <section className="card-premium p-8 border-red-100">
        <div className="flex items-start justify-between gap-6 animate-pulse">
            <div className="flex-1">
                <div className="h-6 bg-red-100 rounded w-32 mb-3"></div>
                <div className="h-4 bg-slate-200 rounded w-full max-w-lg"></div>
            </div>
            <div className="h-11 bg-red-100 rounded-xl w-40"></div>
        </div>
    </section>
);

export const CompanySettingsPage: React.FC = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [loadingProfile, setLoadingProfile] = useState(true);
    const [updatingProfile, setUpdatingProfile] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

    // Profile state
    const [companyName, setCompanyName] = useState('');
    const [initialCompanyName, setInitialCompanyName] = useState('');
    const [fullName, setFullName] = useState('');
    const [initialFullName, setInitialFullName] = useState('');
    const [address, setAddress] = useState('');
    const [initialAddress, setInitialAddress] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [initialPhoneNumber, setInitialPhoneNumber] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [initialJobTitle, setInitialJobTitle] = useState('');
    const [country, setCountry] = useState('');
    const [initialCountry, setInitialCountry] = useState('');
    const [primaryHiringLocation, setPrimaryHiringLocation] = useState('');
    const [initialPrimaryHiringLocation, setInitialPrimaryHiringLocation] = useState('');
    const [companySize, setCompanySize] = useState('');
    const [initialCompanySize, setInitialCompanySize] = useState('');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingAccount, setDeletingAccount] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Redirect if not authenticated
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/');
        }
    }, [user, authLoading, navigate]);

    // Fetch company profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoadingProfile(true);
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                const response = await fetch(`${apiUrl}/api/company/profile`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userData: {
                            sub: user?.id,
                            email: user?.email,
                        },
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to fetch profile');
                }

                const profile = data.profile;
                setCompanyName(profile.name || '');
                setInitialCompanyName(profile.name || '');
                setFullName(profile.full_name || '');
                setInitialFullName(profile.full_name || '');
                setAddress(profile.address || '');
                setInitialAddress(profile.address || '');
                setPhoneNumber(profile.phone_number || '');
                setInitialPhoneNumber(profile.phone_number || '');
                setJobTitle(profile.job_title || '');
                setInitialJobTitle(profile.job_title || '');
                setCountry(profile.country || '');
                setInitialCountry(profile.country || '');
                setPrimaryHiringLocation(profile.primary_hiring_location || '');
                setInitialPrimaryHiringLocation(profile.primary_hiring_location || '');
                setCompanySize(profile.company_size || '');
                setInitialCompanySize(profile.company_size || '');
            } catch (err) {
                console.error('Error fetching profile:', err);
                setProfileError(err instanceof Error ? err.message : 'Failed to load profile');
            } finally {
                setLoadingProfile(false);
            }
        };

        if (user) {
            fetchProfile();
        }
    }, [user]);

    const handleUpdateProfile = async () => {
        setProfileError(null);
        setProfileSuccess(null);

        if (!companyName.trim()) {
            setProfileError('Company name is required');
            return;
        }

        try {
            setUpdatingProfile(true);
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/company/profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                    companyName,
                    fullName,
                    address,
                    phoneNumber,
                    jobTitle,
                    country,
                    primaryHiringLocation,
                    companySize,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update profile');
            }

            // Update initial values to match current values
            setInitialCompanyName(companyName);
            setInitialFullName(fullName);
            setInitialAddress(address);
            setInitialPhoneNumber(phoneNumber);
            setInitialJobTitle(jobTitle);
            setInitialCountry(country);
            setInitialPrimaryHiringLocation(primaryHiringLocation);
            setInitialCompanySize(companySize);

            setProfileSuccess('Profile updated successfully!');

            setTimeout(() => {
                setProfileSuccess(null);
            }, 3000);
        } catch (err) {
            setProfileError(err instanceof Error ? err.message : 'Failed to update profile');
        } finally {
            setUpdatingProfile(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteError(null);
        setDeletingAccount(true);

        try {
            console.log('[CompanySettings] Initiating account deletion for user:', user?.id);

            // Call backend API to delete hirer account and all associated data
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(`${apiUrl}/api/hirer/delete-account`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: {
                        sub: user?.id,
                        email: user?.email,
                    },
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete account');
            }

            console.log('[CompanySettings] Account deleted successfully');

            // Sign out the user (this clears the session)
            await supabase.auth.signOut();

            // Redirect to home page
            navigate('/');
        } catch (err) {
            console.error('[CompanySettings] Account deletion error:', err);
            setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
            setDeletingAccount(false);
        }
    };

    const hasProfileChanged = () => {
        return (
            companyName !== initialCompanyName ||
            fullName !== initialFullName ||
            address !== initialAddress ||
            phoneNumber !== initialPhoneNumber ||
            jobTitle !== initialJobTitle ||
            country !== initialCountry ||
            primaryHiringLocation !== initialPrimaryHiringLocation ||
            companySize !== initialCompanySize
        );
    };

    // Show auth loading state with full layout skeleton
    if (authLoading) {
        return (
            <div className="dashboard-layout">
                <CompanySidebar />
                <div className="main-content">
                    <div className="max-w-7xl mx-auto">
                        <HeaderSkeleton />
                        <div className="space-y-8">
                            <ProfileCardSkeleton />
                            <DeleteCardSkeleton />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <div className="dashboard-layout">
            <CompanySidebar />

            <div className="main-content">
                <div className="w-full max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8">
                    {/* Header - show skeleton if still loading profile */}
                    {loadingProfile ? (
                        <HeaderSkeleton />
                    ) : (
                        <div className="mb-6 md:mb-10 animate-fade-in">
                            <h1 className="heading-1 mb-2">Settings</h1>
                            <p className="text-body text-base md:text-lg">
                                Manage your company profile and account settings.
                            </p>
                        </div>
                    )}

                    <div className="space-y-6 md:space-y-8 animate-fade-in animate-delay-100">
                        {/* Company Profile Section */}
                        {loadingProfile ? (
                            <ProfileCardSkeleton />
                        ) : (
                            <section className="card-premium p-6 md:p-8">
                                <div className="flex items-center justify-between mb-4 md:mb-6">
                                    <div>
                                        <h2 className="heading-2 mb-1">Profile</h2>
                                        <p className="text-xs md:text-sm text-slate-500">Update your company and personal information</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {/* Company Information */}
                                    <div>
                                        <h3 className="text-sm font-semibold text-slate-700 mb-3 md:mb-4">Company Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                            <div className="space-y-2">
                                                <label htmlFor="companyName" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Company Name <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    id="companyName"
                                                    type="text"
                                                    value={companyName}
                                                    onChange={(e) => setCompanyName(e.target.value)}
                                                    placeholder="Enter company name"
                                                    className="input-premium text-sm md:text-base"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label htmlFor="companyEmail" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Email Address
                                                </label>
                                                <input
                                                    id="companyEmail"
                                                    type="email"
                                                    value={user?.email || ''}
                                                    disabled
                                                    className="input-premium bg-slate-50 cursor-not-allowed text-sm md:text-base"
                                                />
                                                <p className="text-xs text-slate-500">Email cannot be changed</p>
                                            </div>

                                            <div className="space-y-2">
                                                <label htmlFor="companySize" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Company Size
                                                </label>
                                                <select
                                                    id="companySize"
                                                    value={companySize}
                                                    onChange={(e) => setCompanySize(e.target.value)}
                                                    className="input-premium text-sm md:text-base"
                                                >
                                                    <option value="">Select size (optional)</option>
                                                    {COMPANY_SIZES.map((size) => (
                                                        <option key={size} value={size}>
                                                            {size}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="space-y-2">
                                                <label htmlFor="primaryHiringLocation" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Primary Hiring Location
                                                </label>
                                                <select
                                                    id="primaryHiringLocation"
                                                    value={primaryHiringLocation}
                                                    onChange={(e) => setPrimaryHiringLocation(e.target.value)}
                                                    className="input-premium text-sm md:text-base"
                                                >
                                                    <option value="">Select a region</option>
                                                    {HIRING_REGIONS.map((region) => (
                                                        <option key={region} value={region}>
                                                            {region}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Personal Information */}
                                    <div className="border-t border-slate-100 pt-6">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-3 md:mb-4">Personal Information</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                            <div className="space-y-2">
                                                <label htmlFor="fullName" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Full Name
                                                </label>
                                                <input
                                                    id="fullName"
                                                    type="text"
                                                    value={fullName}
                                                    onChange={(e) => setFullName(e.target.value)}
                                                    placeholder="e.g. John Smith"
                                                    className="input-premium text-sm md:text-base"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label htmlFor="jobTitle" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Your Job Title
                                                </label>
                                                <input
                                                    id="jobTitle"
                                                    type="text"
                                                    value={jobTitle}
                                                    onChange={(e) => setJobTitle(e.target.value)}
                                                    placeholder="e.g. Head of Talent Acquisition"
                                                    className="input-premium text-sm md:text-base"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label htmlFor="phoneNumber" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Phone Number
                                                </label>
                                                <input
                                                    id="phoneNumber"
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                                    placeholder="e.g. +44 7700 900123"
                                                    className="input-premium text-sm md:text-base"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label htmlFor="country" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Country
                                                </label>
                                                <select
                                                    id="country"
                                                    value={country}
                                                    onChange={(e) => setCountry(e.target.value)}
                                                    className="input-premium text-sm md:text-base"
                                                >
                                                    <option value="">Select your country</option>
                                                    {COUNTRIES.map((countryName) => (
                                                        <option key={countryName} value={countryName}>
                                                            {countryName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="space-y-2 md:col-span-2">
                                                <label htmlFor="address" className="block text-xs md:text-sm font-medium text-slate-700">
                                                    Address
                                                </label>
                                                <input
                                                    id="address"
                                                    type="text"
                                                    value={address}
                                                    onChange={(e) => setAddress(e.target.value)}
                                                    placeholder="e.g. 123 Main Street, London, UK"
                                                    className="input-premium text-sm md:text-base"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-4">
                                        <button
                                            onClick={handleUpdateProfile}
                                            disabled={updatingProfile || !hasProfileChanged()}
                                            className={`btn-primary min-w-[140px] flex items-center justify-center gap-2 ${!hasProfileChanged() && !updatingProfile ? 'opacity-50 cursor-not-allowed' : ''
                                                }`}
                                        >
                                            {updatingProfile ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    <span>Updating...</span>
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </button>
                                    </div>

                                    {/* Success/Error Messages */}
                                    {profileSuccess && (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 animate-fade-in">
                                            <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <p className="text-sm text-green-700 font-medium">{profileSuccess}</p>
                                        </div>
                                    )}

                                    {profileError && (
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                                            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <p className="text-sm text-red-700 font-medium">{profileError}</p>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Account Deletion Section */}
                        {loadingProfile ? (
                            <DeleteCardSkeleton />
                        ) : (
                            <section className="card-premium p-6 md:p-8 border-red-100">
                                <div className="flex flex-col sm:flex-row items-start justify-between gap-4 md:gap-6">
                                    <div className="flex-1">
                                        <h2 className="text-lg md:text-xl font-bold text-red-600 mb-2">Delete Account</h2>
                                        <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                                            Permanently delete your company account, all job postings, and associated data. This action cannot be undone.
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="px-4 md:px-6 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-sm whitespace-nowrap flex-shrink-0 w-full sm:w-auto"
                                    >
                                        Delete My Account
                                    </button>
                                </div>

                                {deleteError && (
                                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in">
                                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-red-700 font-medium">{deleteError}</p>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 shadow-2xl transform transition-all scale-100 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-start gap-3 md:gap-4 mb-4 md:mb-6">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 md:w-6 md:h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">
                                    Delete Account
                                </h3>
                                <p className="text-xs md:text-sm text-slate-600 leading-relaxed">
                                    Are you sure you want to delete your account? This will permanently delete:
                                </p>
                                <ul className="mt-3 md:mt-4 space-y-1.5 md:space-y-2 text-xs md:text-sm text-slate-600 list-disc list-inside bg-red-50 p-3 md:p-4 rounded-lg border border-red-100">
                                    <li>Your company profile</li>
                                    <li>All job postings</li>
                                    <li>All candidate applications</li>
                                    <li>Your account credentials</li>
                                </ul>
                                <p className="mt-3 md:mt-4 text-xs md:text-sm font-semibold text-red-600">
                                    This action cannot be undone.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteError(null);
                                }}
                                disabled={deletingAccount}
                                className="flex-1 px-4 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    handleDeleteAccount();
                                }}
                                disabled={deletingAccount}
                                className="flex-1 px-4 py-2.5 md:py-3 text-xs md:text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {deletingAccount ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    'Delete Account'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
